import { CitationWithId } from '@sciflow/cite';
import { Reference } from '../reference.model';
import { ReferenceSortOrder } from '../store/references.reducer';

/**
 * Sorts references based on selected order using stable source-index fallback.
 */
export const sortReferences = <T extends Reference>(
  references: T[],
  sortOrder: ReferenceSortOrder,
  appearanceIndexById: Map<string, number>,
): T[] => {
  const referencesWithSourceIndex = references.map((reference, sourceIndex) => ({
    reference,
    sourceIndex,
  }));

  if (sortOrder === 'author') {
    return [...referencesWithSourceIndex]
      .sort((left, right) => compareReferencesByAuthor(left.reference, right.reference))
      .map(({ reference }) => reference);
  }

  return [...referencesWithSourceIndex]
    .sort((left, right) => {
      const leftIndex = resolveAppearanceIndex(left.reference, appearanceIndexById);
      const rightIndex = resolveAppearanceIndex(right.reference, appearanceIndexById);
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
      return left.sourceIndex - right.sourceIndex;
    })
    .map(({ reference }) => reference);
};

/**
 * Builds reference appearance index map from citation list order.
 */
export const buildReferenceAppearanceIndexByCitationOrder = (
  citations: CitationWithId[] = [],
): Map<string, number> => {
  const appearanceByReferenceId = new Map<string, number>();
  let nextIndex = 0;

  for (const citation of citations || []) {
    const citationItems = Array.isArray(citation?.citationItems) ? citation.citationItems : [];
    for (const item of citationItems) {
      const candidateIds = [
        typeof item?.id === 'string' ? item.id.trim() : '',
        typeof item?.itemData?.id === 'string' ? item.itemData.id.trim() : '',
      ].filter(Boolean);
      for (const referenceId of candidateIds) {
        if (appearanceByReferenceId.has(referenceId)) {
          continue;
        }
        appearanceByReferenceId.set(referenceId, nextIndex);
        nextIndex += 1;
      }
    }
  }

  return appearanceByReferenceId;
};

/**
 * Compares references by author surname with deterministic fallback text.
 */
export const compareReferencesByAuthor = (a: Reference, b: Reference): number => {
  const aName = a?.csl?.author?.[0]?.family || resolveSortFallback(a);
  const bName = b?.csl?.author?.[0]?.family || resolveSortFallback(b);
  return aName.localeCompare(bName);
};

/**
 * Resolves first appearance index among all known reference identifiers.
 */
export const resolveAppearanceIndex = (
  reference: Reference,
  appearanceIndexById: Map<string, number>,
): number => {
  const candidateIndexes = getReferenceIds(reference)
    .map((referenceId) => appearanceIndexById.get(referenceId))
    .filter((index): index is number => typeof index === 'number');
  if (candidateIndexes.length === 0) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.min(...candidateIndexes);
};

/**
 * Resolves sort fallback text used when author family name is unavailable.
 */
export const resolveSortFallback = (reference: Reference): string => {
  const rawCitation = reference?.csl?.rawCitation ? String(reference.csl.rawCitation).trim() : '';
  if (rawCitation.length > 0) {
    return rawCitation;
  }
  const bib = reference?.bib ? String(reference.bib).trim() : '';
  if (bib.length > 0) {
    return bib;
  }
  const title = reference?.csl?.title ? String(reference.csl.title).trim() : '';
  if (title.length > 0) {
    return title;
  }
  return '';
};

/**
 * Collects all supported identifiers that can address a reference.
 */
export const getReferenceIds = (reference: Reference): string[] => {
  const ids = [reference?.csl?.id, reference?.key]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);
  return Array.from(new Set(ids));
};
